import React from 'react';
import { Loader2, CheckCircle, AlertCircle, FileText, Upload, Search, BarChart3 } from 'lucide-react';

interface ProcessingProgressProps {
  progress: number;
  status: string;
  attempt?: number;
  isComplete?: boolean;
  hasError?: boolean;
  errorMessage?: string;
}

const ProcessingProgress: React.FC<ProcessingProgressProps> = ({
  progress,
  status,
  attempt,
  isComplete = false,
  hasError = false,
  errorMessage
}) => {
  const getProgressIcon = () => {
    if (hasError) {
      return <AlertCircle className="w-8 h-8 text-red-500" />;
    }
    
    if (isComplete) {
      return <CheckCircle className="w-8 h-8 text-green-500" />;
    }

    // Progress-based icons
    if (progress < 30) {
      return <Upload className="w-8 h-8 text-blue-500 animate-pulse" />;
    } else if (progress < 60) {
      return <Search className="w-8 h-8 text-blue-500 animate-pulse" />;
    } else if (progress < 90) {
      return <FileText className="w-8 h-8 text-blue-500 animate-pulse" />;
    } else {
      return <BarChart3 className="w-8 h-8 text-blue-500 animate-pulse" />;
    }
  };

  const getProgressColor = () => {
    if (hasError) return 'bg-red-500';
    if (isComplete) return 'bg-green-500';
    if (progress < 30) return 'bg-blue-400';
    if (progress < 60) return 'bg-blue-500';
    if (progress < 90) return 'bg-blue-600';
    return 'bg-green-400';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4">
        <div className="text-center">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            {getProgressIcon()}
          </div>

          {/* Title */}
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {hasError ? 'Upload Failed' : 
             isComplete ? 'Upload Successful!' :
             'Uploading PDF Statement'}
          </h2>

          {/* Progress Stage
          <p className="text-sm text-gray-600 mb-4">
            {getProgressStage()}
          </p> */}

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
            <div 
              className={`h-3 rounded-full transition-all duration-300 ${getProgressColor()}`}
              style={{ width: `${Math.max(progress, 0)}%` }}
            ></div>
          </div>

          {/* Progress Percentage */}
          <div className="flex justify-between text-sm text-gray-600 mb-4">
            <span>{Math.round(progress)}%</span>
            <span>{isComplete ? '100%' : `${Math.round(progress)}/100`}</span>
          </div>

          {/* Status Message */}
          <div className="bg-gray-50 rounded-lg p-3 mb-4">
            <p className="text-sm text-gray-700 flex items-center justify-center">
              {!hasError && !isComplete && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              {hasError ? errorMessage : status}
            </p>
          </div>

          {/* Attempt Info
          {attempt && !isComplete && !hasError && (
            <p className="text-xs text-gray-500">
              Checking progress... (Attempt {attempt})
            </p>
          )} */}


          
          {/* Upload steps */}
          <div className="mt-6 space-y-2">
            <div className="flex items-center text-sm">
              <div className={`w-3 h-3 rounded-full mr-3 ${
                progress >= 50 ? 'bg-green-500' : progress >= 5 ? 'bg-yellow-500' : 'bg-gray-300'
              }`}></div>
              <span className={progress >= 50 ? 'text-green-600' : 'text-gray-500'}>
                PDF Upload
              </span>
            </div>
            <div className="flex items-center text-sm">
              <div className={`w-3 h-3 rounded-full mr-3 ${
                progress >= 100 ? 'bg-green-500' : progress >= 70 ? 'bg-yellow-500' : 'bg-gray-300'
              }`}></div>
              <span className={progress >= 100 ? 'text-green-600' : 'text-gray-500'}>
                File Validation
              </span>
            </div>
          </div>

          {/* Upload completion message */}
          {isComplete && (
            <div className="mt-4 text-xs text-green-600 bg-green-50 p-3 rounded-lg">
              <p className="font-medium">PDF Upload successful!</p>
              <p>Use the "Check Status" button to see when analysis is ready.</p>
            </div>
          )}

          {/* Error Actions */}
          {hasError && (
            <div className="mt-6 space-y-3">
              <button 
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm"
              >
                Try Again
              </button>
              
              {/* Show additional help for service unavailable errors */}
              {errorMessage?.includes('temporarily unavailable') && (
                <div className="text-xs text-gray-600 bg-gray-50 p-3 rounded-lg">
                  <p className="font-medium mb-1">Troubleshooting tips:</p>
                  <ul className="space-y-1">
                    <li>• Wait a few minutes and try "Check Status" again</li>
                    <li>• If issue persists, use "Start New Analysis" to re-upload</li>
                    <li>• The service may be under maintenance</li>
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProcessingProgress;
